import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CacheKeyEnum, Channel, ConfirmationComponent, Pager, WarningWord, WarningWordService } from 'lib-trend-core';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';
import { WarningWordEditComponent } from './warning-word-edit/warning-word-edit.component';

@Component({
    selector: 'warning-word-component',
    // templateUrl: 'warning-word.component.html',
    templateUrl: 'warning-word.v2.component.html',
    styleUrl: 'warning-word.v2.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WarningWordComponent extends AbstractComponent implements OnInit {
  pager: Pager<WarningWord> = new Pager<WarningWord>({ perPage: 10 });
  listObservable: Observable<Pager<WarningWord>>;
  defaultValue: string = '';
  private termoDaBusca: Subject<string> = new Subject<string>();

  channel!: Channel;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    public warningWordService: WarningWordService,
  ) {
    super(injector);
    this.setupForm();
    this.loadingContent = true;
    this.cacheKey = CacheKeyEnum.WARNING_WORDS;
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const cached = this.restoreState<WarningWord>();
    if (!cached) {
      this.getList();
      return;
    }
    this.pager = cached.pager;
    this.searchString = cached.search ?? '';
    this.loadingContent = false;

    if (this.searchString) {
      this.formGroup.get('search').setValue(this.searchString);
    }

    this.setupObservableSearch();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      search: [''],
    });
  }

  private setupObservableSearch(): void {
    this.listObservable = this.termoDaBusca.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(term => {
        this.searchString = term;
        return this.warningWordService.getAll(this.pager.page, this.pager.perPage, term, this.searchParams);
      })
    );
    this.listObservable.subscribe((pager: Pager<WarningWord>) => {
      this.pager = pager;
      this.persistListState(this.pager, this.searchString, this.searchParams);
    });
  }

  search(termo: string) {
    this.pager.page = 1;
    this.termoDaBusca.next(termo);
  }

  add(): void {
    const dialogRef = this.dialog.open(WarningWordEditComponent, {
      width: '800px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.getList();
    });
  }

  edit(item: WarningWord): void {
    const dialogRef = this.dialog.open(WarningWordEditComponent, {
      width: '800px',
      data: item
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.getList();
    });
  }

  delete(item: WarningWord): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '800px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.warningWordService.delete(item._id).subscribe({
          next: () => {
            this.alertService.success(this.t('warningWord.list.deleteSuccess'));
            this.getList();
          },
          error: (err) => this.alertService.error(err.error.message)
        });
      }
    });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  private getList() {
    this.loadingContent = true;
    this.warningWordService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams).subscribe({
      next: (value) => {
        this.pager = value;
        this.loadingContent = false;
        this.setupObservableSearch();
        this.persistListState(this.pager, this.searchString, this.searchParams);
      },
      error: (err) => {
        this.alertService.error(err.error.message);
        this.loadingContent = false;
      },
    });
  }
}
